import { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import {
  Button,
  Callout,
  Dialog,
  InputGroup,
  Spinner,
} from "@blueprintjs/core";
import {
  faEnvelopeSquare,
  faUserPlus,
  faUsersCog,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { LinkButton } from "@components";
import { useBasePath, useInvitationMutation, UserInviteRole } from "@hooks";
import { Organization, OrgData } from "@types";
import { withToaster } from "@utils";

import { fetchOrganization } from "../../api";

const Wrapper = styled.div`
  width: 862px;
  padding-top: 20px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Form = styled.form`
  display: flex;
  gap: 8px;
  justify-content: center;
  margin-bottom: 40px;
  margin-top: 20px;
`;

const Headline = styled.h1`
  font-weight: normal;
  font-size: 32px;
  margin-bottom: 8px;
`;

const BodyText = styled.p`
  max-width: 500px;
`;

const OrgNameParagraph = styled.p`
  min-height: 40px;
`;

const roles = {
  member: {
    title: "as member",
  },
  admin: {
    title: "as admin",
  },
};

type Props = { isOpen: boolean; onClose: () => void; deployment: OrgData };

export function NewTeamMemberDialog({
  isOpen,
  onClose: onCloseOuter,
  deployment,
}: Props) {
  const [email, setEmail] = useState<string>("");
  const [parentOrganization, setParentOrganization] = useState<
    Organization | undefined
  >(undefined);
  const [role, setRole] = useState<UserInviteRole>("member");
  const emailRef = useRef<HTMLInputElement>(null);
  const { mutate, data, error, isLoading, reset } = useInvitationMutation();
  const history = useHistory();
  const basePath = useBasePath();

  useEffect(() => {
    withToaster(
      () => {
        if (!deployment?.organization_id) return Promise.resolve();
        return fetchOrganization(deployment.organization_id).then(
          setParentOrganization
        );
      },
      (err) => `Failed to load organization: ${err.body?.error}`
    )();
  }, [deployment]);

  function resetAll() {
    reset();
    setEmail("");
  }
  function onClose() {
    resetAll();
    onCloseOuter();
  }

  return (
    <Dialog
      isOpen={isOpen}
      title={
        <>
          <FontAwesomeIcon
            icon={faUsersCog}
            size="sm"
            style={{ marginRight: "8px" }}
          />
          Add Team Members
        </>
      }
      onClose={onClose}
    >
      <Wrapper>
        {isLoading ? (
          <div
            style={{
              margin: "60px",
            }}
          >
            <Spinner></Spinner>
          </div>
        ) : error ? (
          <div>
            <Callout intent="danger">Error: {String(error)}</Callout>
          </div>
        ) : data ? (
          <>
            <FontAwesomeIcon
              color={"var(--global-purple)"}
              icon={faEnvelopeSquare}
              size="4x"
              style={{
                marginTop: "40px",
              }}
            />
            <Headline>Invite sent to {email}</Headline>
            <Form>
              <Button large intent="primary" onClick={() => resetAll()}>
                Invite another
              </Button>
              <Button large intent="primary" minimal onClick={onClose}>
                Done
              </Button>
            </Form>
          </>
        ) : (
          <>
            <FontAwesomeIcon
              color={"var(--global-purple)"}
              icon={faUserPlus}
              size="3x"
              style={{
                marginTop: "20px",
              }}
            />
            <Headline>Invite A Team Member!</Headline>
            <BodyText>
              Invite a teammate and they’ll be able to explore findings for all
              of the projects assigned to this team.
            </BodyText>
            <OrgNameParagraph>
              {parentOrganization &&
                `The invitee will see the name of your organization as "${parentOrganization.name}". `}
              {parentOrganization && (
                <sup>
                  <LinkButton
                    onClick={() =>
                      history.push({
                        pathname: `${basePath}/settings/id`,
                      })
                    }
                  >
                    change
                  </LinkButton>
                </sup>
              )}
            </OrgNameParagraph>
            <Form
              onSubmit={() => {
                mutate({
                  user_invite: {
                    invited_email: email,
                    initial_role: role,
                  },
                });
              }}
            >
              <InputGroup
                name="email"
                asyncControl={true}
                large={true}
                onChange={(e) => {
                  setEmail(e.target.value);
                }}
                placeholder="yourteammate@email.com"
                leftIcon="envelope"
                value={email}
                type="email"
                required
                inputRef={emailRef}
              />
              <div className="bp3-html-select bp3-large">
                <select
                  onChange={(e) => {
                    setRole(e.target.value as UserInviteRole);
                  }}
                >
                  {Object.entries(roles).map(([roleKey, { title }]) => {
                    return (
                      <option
                        key={roleKey}
                        value={roleKey}
                        selected={roleKey === role}
                      >
                        {title}
                      </option>
                    );
                  })}
                </select>
                <span className="bp3-icon bp3-icon-double-caret-vertical"></span>
              </div>
              <Button
                disabled={!emailRef.current?.validity?.valid}
                intent="primary"
                type="submit"
              >
                Invite
              </Button>
            </Form>

            <Button intent="primary" minimal onClick={onClose}>
              Cancel
            </Button>
          </>
        )}
      </Wrapper>
    </Dialog>
  );
}
